// Phase 0089 — Live Study Capture page (TIMER_PLUS_COUNTER + TIMER + COUNTER).
//
// Shop-floor UX rules (UI/UX Pro Max):
//   - One-hand usable on a 5"–6" phone
//   - Huge tap target (full-screen-width, ~40% screen height)
//   - 60-second countdown in 96px font
//   - No keyboards, no dropdowns during capture
//   - Auto-stop at planned_duration. Late taps are rejected with a buzz.
//   - Undo last tap is a small button (mistake-tolerant)
//   - Submit / Retake buttons only appear AFTER timer stops
//   - High contrast: black background while running

import { useEffect, useMemo, useRef, useState } from 'react'
import {
  App, Alert, Button, Modal, Space, Tag, Typography,
} from 'antd'
import { LeftOutlined, UndoOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'

import { liveStudyApi } from '@/features/hr/liveStudyApi'

const { Text, Title } = Typography

export default function LiveStudyCapturePage() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { message } = App.useApp()

  // Local counter mirrors backend taps for instant feedback
  const [localTaps, setLocalTaps] = useState(0)
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null)
  const [running, setRunning] = useState(false)
  const [retakeOpen, setRetakeOpen] = useState(false)
  const [retakeReason, setRetakeReason] = useState('')
  const stopRef = useRef<number | null>(null)

  const { data: bundle, isLoading, refetch } = useQuery({
    queryKey: ['study-session', sessionId],
    queryFn: () => liveStudyApi.getSession(sessionId!),
    enabled: !!sessionId,
  })

  const tmpl = bundle?.template
  const sess = bundle?.session
  const result = bundle?.result
  const planned = tmpl?.default_duration_seconds ?? 60

  // ── Mutations ──
  const startTimerMut = useMutation({
    mutationFn: () => liveStudyApi.startTimer(sessionId!),
    onSuccess: () => {
      setLocalTaps(0)
      setSecondsLeft(planned)
      setRunning(true)
      refetch()
    },
    onError: (e: Error) => message.error(e.message),
  })

  const tapMut = useMutation({
    mutationFn: () => liveStudyApi.tap(sessionId!),
    onSuccess: (data) => {
      if (data.rejected_after_auto_stop) {
        setRunning(false)
        setSecondsLeft(0)
        message.warning('Auto-stopped — tap ignored')
        refetch()
        return
      }
      setLocalTaps(data.tap_count)
    },
    onError: (e: Error) => message.error(e.message),
  })

  const undoMut = useMutation({
    mutationFn: () => liveStudyApi.undoTap(sessionId!),
    onSuccess: (data) => setLocalTaps(data.tap_count),
  })

  const stopMut = useMutation({
    mutationFn: () => liveStudyApi.stopTimer(sessionId!),
    onSuccess: () => { setRunning(false); refetch() },
  })

  const completeMut = useMutation({
    mutationFn: () => liveStudyApi.complete(sessionId!),
    onSuccess: () => {
      message.success('Study completed. Result computed.')
      refetch()
    },
    onError: (e: Error) => message.error(e.message),
  })

  const retakeMut = useMutation({
    mutationFn: (reason: string) => liveStudyApi.retake(sessionId!, reason),
    onSuccess: () => {
      setRetakeOpen(false); setRetakeReason('')
      setLocalTaps(0); setSecondsLeft(null); setRunning(false)
      message.success('Retake — start the timer again')
      refetch()
    },
    onError: (e: Error) => message.error(e.message),
  })

  // ── Countdown driver ──
  useEffect(() => {
    if (!running) return
    if (secondsLeft === null) return
    if (secondsLeft <= 0) {
      // Trigger backend auto-stop and lock UI
      stopMut.mutate()
      setRunning(false)
      return
    }
    const t = window.setTimeout(() => setSecondsLeft((s) =>
      s === null ? null : s - 1), 1000)
    stopRef.current = t
    return () => window.clearTimeout(t)
  }, [running, secondsLeft, stopMut])

  // ── Restore state if user navigated back ──
  useEffect(() => {
    if (!sess) return
    if (sess.session_status === 'IN_PROGRESS' && sess.start_time) {
      const startedAt = new Date(sess.start_time).getTime()
      const now = Date.now()
      // Clamp elapsed to [0, planned]. Server clock can be ahead of the
      // client by a few hundred ms (network latency + Python writing the
      // timestamp before the HTTP response reaches us), which would make
      // raw elapsed negative and the displayed countdown read `planned + 1`
      // (i.e. 61 for a 60-second study). Same clamp prevents an under-
      // flow if the page is refreshed after the planned window passed.
      const elapsed = Math.min(planned, Math.max(0, Math.floor((now - startedAt) / 1000)))
      const remain = planned - elapsed
      setSecondsLeft(remain)
      setRunning(remain > 0)
      setLocalTaps(bundle?.tap_count ?? 0)
    } else {
      setLocalTaps(bundle?.tap_count ?? 0)
      setRunning(false)
    }
  }, [sess, planned, bundle?.tap_count])

  if (isLoading || !bundle || !sess || !tmpl) {
    return <div style={{ padding: 24 }}><Title level={4}>Loading…</Title></div>
  }

  const isCompleted = ['COMPLETED', 'VERIFIED'].includes(sess.session_status)
  const ctx = (sess.auto_fetched_context || {}) as Record<string, unknown>

  return (
    <div style={{
      minHeight: '100vh',
      background: running ? '#000' : '#fafafa',
      color: running ? '#fff' : '#000',
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <Button
          type="text"
          icon={<LeftOutlined />}
          onClick={() => navigate('/hr/live-studies')}
          style={{ color: running ? '#fff' : 'inherit', fontSize: 18 }}
        >
          Back
        </Button>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <Text strong style={{ fontSize: 16, color: running ? '#fff' : 'inherit' }}>
            {tmpl.template_name}
          </Text>
        </div>
        <Tag color={running ? 'red' : 'default'}>{sess.session_status}</Tag>
      </div>

      {/* Context strip — only when not running, to keep screen calm during capture */}
      {!running && !isCompleted && (
        <Space wrap style={{ marginBottom: 12 }}>
          {ctx.employee_name ? <Tag color="blue">{String(ctx.employee_name)}</Tag> : null}
          {ctx.emp_code ? <Tag>{String(ctx.emp_code)}</Tag> : null}
          {ctx.machine_code ? <Tag color="purple">{String(ctx.machine_code)}</Tag> : null}
          {ctx.top_skill_name ? <Tag color="cyan">{String(ctx.top_skill_name)} L{String(ctx.top_skill_level || '')}</Tag> : null}
          {ctx.top_certification_status ? (
            <Tag color={String(ctx.top_certification_status) === 'CERTIFIED' ? 'green' : 'orange'}>
              {String(ctx.top_certification_status)}
            </Tag>
          ) : null}
        </Space>
      )}

      {/* Live result already computed — show big stat */}
      {isCompleted && result && (
        <div style={{ marginTop: 20, textAlign: 'center' }}>
          <Text type="secondary">RESULT</Text>
          <div style={{ fontSize: 80, fontWeight: 700, lineHeight: 1 }}>
            {Number(result.actual_value ?? 0).toFixed(1)}
          </div>
          <Text>{result.unit}</Text>
          <div style={{ marginTop: 12 }}>
            <Tag color={result.status === 'ABOVE_STANDARD' ? 'green'
                       : result.status === 'ON_TARGET' ? 'blue'
                       : 'red'} style={{ fontSize: 16, padding: '6px 14px' }}>
              {result.status}
            </Tag>
          </div>
          <div style={{ marginTop: 20, fontSize: 14 }}>
            <Text type="secondary">Standard: </Text>
            <Text strong>{Number(result.standard_value ?? 0).toFixed(1)} {result.unit}</Text>
            {' · '}
            <Text type="secondary">Gap: </Text>
            <Text strong>
              {result.gap_value !== null && result.gap_value !== undefined
                ? `${Number(result.gap_value).toFixed(1)} (${Number(result.gap_pct ?? 0).toFixed(1)}%)`
                : '—'}
            </Text>
          </div>
          {result.training_required && (
            <Alert showIcon style={{ marginTop: 20 }} type="warning"
                   message="Training recommendation generated"
                   description="A TRAINING CAPA has been queued for HR. Open the History page to track closure." />
          )}
          <Space style={{ marginTop: 24 }} wrap>
            <Button size="large" onClick={() => setRetakeOpen(true)}>Retake</Button>
            <Button size="large" type="primary"
                    onClick={() => navigate('/hr/live-studies')}>
              Done
            </Button>
          </Space>
        </div>
      )}

      {/* Countdown — huge */}
      {!isCompleted && (
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Text style={{ fontSize: 14, color: running ? '#ccc' : '#888' }}>
              {running ? 'RECORDING' : 'READY'}
            </Text>
            <div style={{
              fontSize: 120,
              fontWeight: 700,
              lineHeight: 1,
              color: running ? '#fff' : '#000',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {secondsLeft !== null
                ? Math.max(0, Math.min(planned, secondsLeft))
                : planned}
            </div>
            <Text style={{ fontSize: 14, color: running ? '#ccc' : '#888' }}>
              seconds
            </Text>
          </div>

          {/* Tap counter — full-width huge button */}
          {sess.session_status === 'IN_PROGRESS' || sess.session_status === 'DRAFT' ? (
            <>
              <div style={{ textAlign: 'center', marginBottom: 8 }}>
                <Text style={{ fontSize: 14, color: running ? '#ccc' : '#888' }}>
                  COUNT
                </Text>
                <div style={{
                  fontSize: 96,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: running ? '#52c41a' : '#1677ff',
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {localTaps}
                </div>
              </div>

              {!running ? (
                <Button
                  type="primary"
                  size="large"
                  block
                  style={{
                    height: 100,
                    fontSize: 32,
                    fontWeight: 700,
                  }}
                  onClick={() => startTimerMut.mutate()}
                  loading={startTimerMut.isPending}
                >
                  ▶ START
                </Button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => tapMut.mutate()}
                    disabled={tapMut.isPending}
                    style={{
                      width: '100%',
                      height: 220,
                      fontSize: 36,
                      fontWeight: 800,
                      border: 'none',
                      borderRadius: 24,
                      background: '#52c41a',
                      color: '#fff',
                      cursor: 'pointer',
                      transition: 'transform 0.05s',
                      boxShadow: '0 8px 0 #389e0d',
                    }}
                    onMouseDown={(e) => (e.currentTarget.style.transform = 'translateY(4px)')}
                    onMouseUp={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
                    onTouchStart={(e) => (e.currentTarget.style.transform = 'translateY(4px)')}
                    onTouchEnd={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
                  >
                    {tmpl.counter_label || 'TAP'}
                  </button>

                  <Space style={{ marginTop: 12, width: '100%', justifyContent: 'space-between' }}>
                    <Button
                      icon={<UndoOutlined />}
                      onClick={() => undoMut.mutate()}
                      style={{ color: '#fff', borderColor: '#666', background: '#222' }}
                    >
                      Undo
                    </Button>
                    <Button
                      onClick={() => stopMut.mutate()}
                      style={{ color: '#fff', borderColor: '#666', background: '#222' }}
                    >
                      Stop Early
                    </Button>
                  </Space>
                </>
              )}
            </>
          ) : sess.session_status === 'PAUSED' ? (
            <div style={{ textAlign: 'center' }}>
              <Title level={3}>Timer stopped — {localTaps} taps recorded</Title>
              <Space size="middle" style={{ marginTop: 16 }} wrap>
                <Button size="large" onClick={() => setRetakeOpen(true)}>Retake</Button>
                <Button size="large" type="primary"
                        onClick={() => completeMut.mutate()}
                        loading={completeMut.isPending}>
                  Compute Result →
                </Button>
              </Space>
            </div>
          ) : null}
        </div>
      )}

      {/* Retake reason modal */}
      <Modal
        open={retakeOpen}
        title="Retake reason"
        onCancel={() => setRetakeOpen(false)}
        onOk={() => {
          if (!retakeReason.trim()) {
            message.error('Reason required')
            return
          }
          retakeMut.mutate(retakeReason)
        }}
        confirmLoading={retakeMut.isPending}
      >
        <Text>Why are you retaking this study?</Text>
        <textarea
          value={retakeReason}
          onChange={(e) => setRetakeReason(e.target.value)}
          rows={3}
          style={{ width: '100%', marginTop: 8, padding: 8 }}
          placeholder="Operator distracted, timer started early, machine stopped, etc."
        />
      </Modal>
    </div>
  )
}
